import { useEffect, useRef, useState } from 'react';
import {
  MappingJob,
  MAPPING_JOB_TERMINAL_STATUSES,
} from '../types/dynamodb';
import { NexusMappingJobService } from '../services/nexus-api';

interface Options {
  intervalMs?: number;
  enabled?: boolean;
}

/**
 * Polls the mapping-jobs list endpoint at a fixed interval.
 * Continues polling while at least one job is in a non-terminal state,
 * and stops once everything is COMPLETED or FAILED.
 */
export function useMappingJobsPolling(
  service: NexusMappingJobService,
  { intervalMs = 10_000, enabled = true }: Options = {},
): {
  jobs: MappingJob[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<MappingJob[] | null>;
} {
  const [jobs, setJobs] = useState<MappingJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const cancelRef = useRef(false);

  const fetchOnce = async (): Promise<MappingJob[] | null> => {
    setLoading(true);
    try {
      const next = await service.listJobs();
      if (!cancelRef.current) {
        setJobs(next);
        setError(null);
      }
      return next;
    } catch (err) {
      if (!cancelRef.current) {
        setError(err as Error);
      }
      return null;
    } finally {
      if (!cancelRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!enabled || !service.isEnabled()) return;
    cancelRef.current = false;
    let handle: ReturnType<typeof setTimeout> | null = null;

    const loop = async () => {
      const next = await fetchOnce();
      if (cancelRef.current) return;
      const anyActive = (next ?? []).some(
        (j) => !MAPPING_JOB_TERMINAL_STATUSES.includes(j.status),
      );
      // Always schedule the next tick — new jobs may enter the queue even
      // when the current list is all terminal.
      handle = setTimeout(loop, anyActive ? intervalMs : intervalMs * 3);
    };

    loop();
    return () => {
      cancelRef.current = true;
      if (handle) clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, service]);

  return { jobs, loading, error, refresh: fetchOnce };
}
